import { useI18n } from '@affine/i18n';
import { streamLainAnimationController } from '@blocksuite/affine/shared/utils';
import {
  Fragment,
  type HTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import {
  type SettingsPageWidth,
  type SettingsTabItem,
  type SettingsTabsProps,
  useSettingsFrame,
  useSettingsPageWidth,
} from './settings-frame';
import * as styles from './settings-page.css';
import {
  type SettingsPageReadiness,
  SettingsPageReadinessContext,
} from './settings-page-readiness';
import { SettingsRailContext, type SettingsRailItem } from './settings-rail';
import { SettingsPageLoader } from './settings-states';

/** Sub-tabs of a settings page. Arrow keys move, the selected tab is the only tab stop. */
export const SettingsTabs = ({
  tabs,
  activeTab,
  onChange,
  ariaLabel,
}: SettingsTabsProps) => {
  const listRef = useRef<HTMLDivElement>(null);
  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      const enabled = tabs.filter(tab => !tab.disabled);
      const index = enabled.findIndex(tab => tab.id === activeTab);
      const target =
        event.key === 'ArrowRight'
          ? enabled[(index + 1) % enabled.length]
          : event.key === 'ArrowLeft'
            ? enabled[(index - 1 + enabled.length) % enabled.length]
            : event.key === 'Home'
              ? enabled[0]
              : event.key === 'End'
                ? enabled[enabled.length - 1]
                : undefined;
      if (!target || target.id === activeTab) return;
      event.preventDefault();
      onChange(target.id);
      listRef.current
        ?.querySelector<HTMLElement>(`[data-tab-id="${CSS.escape(target.id)}"]`)
        ?.focus();
    },
    [tabs, activeTab, onChange]
  );

  if (tabs.length === 0) return null;
  return (
    <div
      ref={listRef}
      className={styles.tabList}
      role="tablist"
      aria-label={ariaLabel}
      onKeyDown={onKeyDown}
    >
      {tabs.map(tab => {
        const selected = tab.id === activeTab;
        const button = (
          <button
            key={tab.id}
            type="button"
            role="tab"
            className={styles.tab}
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            data-active={selected}
            data-tab-id={tab.id}
            data-testid={tab.testId}
            disabled={tab.disabled}
            onClick={() => onChange(tab.id)}
          >
            {tab.label}
          </button>
        );
        return tab.wrap ? (
          <Fragment key={tab.id}>{tab.wrap(button)}</Fragment>
        ) : (
          button
        );
      })}
    </div>
  );
};

export interface SettingsPageHeaderProps extends Omit<
  HTMLAttributes<HTMLElement>,
  'title'
> {
  title: ReactNode;
  description?: ReactNode;
  /** Marker next to the title, e.g. a plan or beta chip. */
  badge?: ReactNode;
  actions?: ReactNode;
  tabs?: readonly SettingsTabItem[];
  activeTab?: string;
  onTabChange?: (tabId: string) => void;
  tabsLabel?: string;
}

/**
 * Header of a settings page: path, title, description, actions and sub-tabs,
 * always in this order. Inside the settings dialog it is placed in the fixed
 * area above the scrolling body; elsewhere it renders in place.
 */
export const SettingsPageHeader = ({
  title,
  description,
  badge,
  actions,
  tabs,
  activeTab,
  onTabChange,
  tabsLabel,
  ...props
}: SettingsPageHeaderProps) => {
  const frame = useSettingsFrame();
  const setView = frame?.setView;
  const ownTabs =
    tabs?.length && activeTab && onTabChange
      ? { tabs, activeTab, onChange: onTabChange, ariaLabel: tabsLabel }
      : null;
  const shownTabs = ownTabs ?? frame?.tabs ?? null;
  const hasTabs = Boolean(shownTabs);
  const view = ownTabs?.activeTab;
  useLayoutEffect(() => {
    setView?.(view ?? null);
  }, [setView, view]);

  const header = (
    <header className={styles.header} data-tabs={hasTabs} {...props}>
      {frame?.eyebrow ? (
        <div className={styles.eyebrow}>{frame.eyebrow}</div>
      ) : null}
      <div className={styles.titleRow}>
        <h1 className={styles.title}>{title}</h1>
        {badge}
        {actions ? <div className={styles.actions}>{actions}</div> : null}
      </div>
      {description ? (
        <div className={styles.description}>{description}</div>
      ) : null}
      {shownTabs ? <SettingsTabs {...shownTabs} /> : null}
    </header>
  );
  if (!frame) return header;
  return frame.headerSlot ? createPortal(header, frame.headerSlot) : null;
};

const SettingsRail = ({ items }: { items: readonly SettingsRailItem[] }) => {
  const title = useI18n()['com.streamlain.settings.ui.rail.title']();
  const scrollRoot = useSettingsFrame()?.scrollRoot ?? null;
  const [active, setActive] = useState(items[0]?.id);
  const ids = items.map(item => item.id).join('\n');

  useEffect(() => {
    if (!scrollRoot) return;
    const visible = new Set<string>();
    const order = ids.split('\n');
    const observer = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          if (entry.isIntersecting) visible.add(entry.target.id);
          else visible.delete(entry.target.id);
        }
        const first = order.find(id => visible.has(id));
        if (first) setActive(first);
      },
      { root: scrollRoot, rootMargin: '0px 0px -60% 0px' }
    );
    for (const id of order) {
      const element = scrollRoot.querySelector(`#${CSS.escape(id)}`);
      if (element) observer.observe(element);
    }
    return () => observer.disconnect();
  }, [scrollRoot, ids]);

  return (
    <nav className={styles.rail} aria-label={title}>
      <div className={styles.railTitle}>{title}</div>
      {items.map(item => (
        <button
          key={item.id}
          type="button"
          className={styles.railItem}
          aria-current={item.id === active}
          onClick={() => {
            setActive(item.id);
            scrollRoot
              ?.querySelector(`#${CSS.escape(item.id)}`)
              ?.scrollIntoView({ block: 'start', behavior: 'smooth' });
          }}
        >
          {item.title}
        </button>
      ))}
    </nav>
  );
};

export interface SettingsPageProps extends SettingsPageHeaderProps {
  width?: SettingsPageWidth;
  children: ReactNode;
}

/**
 * A page with more than two sections gets the "On this page" rail; it lists
 * the sections the page renders.
 */
const RAIL_MIN_SECTIONS = 3;

/** Rail stops in the order their sections appear on the page. */
function inPageOrder(
  root: HTMLElement | null,
  items: readonly SettingsRailItem[]
): readonly SettingsRailItem[] {
  if (!root) return items;
  const position = new Map(
    items.map(item => [item.id, root.querySelector(`#${CSS.escape(item.id)}`)])
  );
  return [...items].sort((a, b) => {
    const first = position.get(a.id);
    const second = position.get(b.id);
    if (!first || !second) return first ? -1 : second ? 1 : 0;
    return first.compareDocumentPosition(second) &
      Node.DOCUMENT_POSITION_FOLLOWING
      ? -1
      : 1;
  });
}

/** A page body, and each sub-tab, rises in softly when it opens. */
const PAGE_ENTER: Keyframe[] = [
  { opacity: 0, transform: 'translateY(4px)' },
  { opacity: 1, transform: 'none' },
];

/** A whole settings page: shared header plus a body in the declared width. */
export const SettingsPage = ({
  width = 'wide',
  children,
  ...header
}: SettingsPageProps) => {
  const bodyRef = useRef<HTMLDivElement>(null);
  const [railItems, setRailItems] = useState<readonly SettingsRailItem[]>([]);
  const railRegistry = useMemo(
    () => ({
      register: (item: SettingsRailItem) => {
        setRailItems(items =>
          inPageOrder(bodyRef.current, [
            ...items.filter(other => other.id !== item.id),
            item,
          ])
        );
        return () =>
          setRailItems(items => items.filter(other => other !== item));
      },
    }),
    []
  );
  const hasRail = railItems.length >= RAIL_MIN_SECTIONS;
  const frameWidth = hasRail ? 'wide' : width;
  useSettingsPageWidth(frameWidth);
  const view = header.activeTab;

  // Parts register while they mount, before this page's layout effect runs, so
  // the count is read from a ref; the reducer only re-renders on a change.
  const backendAvailable = useSettingsFrame()?.backendAvailable ?? true;
  const holds = useRef(0);
  const [holdsChanged, onHoldsChange] = useReducer((n: number) => n + 1, 0);
  const [readyView, setReadyView] = useState<{
    view: string | undefined;
  } | null>(null);
  const ready = readyView !== null && readyView.view === view;
  const hold = useCallback(() => {
    holds.current += 1;
    onHoldsChange();
    return () => {
      holds.current -= 1;
      onHoldsChange();
    };
  }, []);
  // Ready once nothing holds the page back. Without the server a local-first
  // page shows what it has instead of waiting for it.
  useLayoutEffect(() => {
    if (ready) return;
    if (holds.current === 0 || !backendAvailable) setReadyView({ view });
  }, [ready, holdsChanged, backendAvailable, view]);
  const readiness = useMemo<SettingsPageReadiness>(
    () => ({ ready, hold }),
    [ready, hold]
  );
  const pageReady = useSettingsFrame()?.pageReady;
  useLayoutEffect(() => {
    if (ready) pageReady?.();
  }, [ready, view, pageReady]);

  useLayoutEffect(() => {
    const element = bodyRef.current;
    if (!ready || !element) return;
    const motion = streamLainAnimationController.play(
      element,
      PAGE_ENTER,
      'enter'
    );
    return () => motion.cancel();
  }, [view, ready]);
  return (
    <>
      <SettingsPageHeader {...header} />
      <div
        key={view}
        ref={bodyRef}
        className={styles.body}
        data-rail={ready && hasRail}
        data-ready={ready}
        data-width={frameWidth}
        role={header.tabs?.length ? 'tabpanel' : undefined}
      >
        <div className={styles.bodyColumn} data-settings-page-body>
          <SettingsPageReadinessContext.Provider value={readiness}>
            <SettingsRailContext.Provider value={railRegistry}>
              {children}
            </SettingsRailContext.Provider>
          </SettingsPageReadinessContext.Provider>
        </div>
        {!ready ? (
          <SettingsPageLoader />
        ) : hasRail ? (
          <SettingsRail items={railItems} />
        ) : null}
      </div>
    </>
  );
};
